import { useState, useEffect } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { FaTimes, FaPlus, FaEdit, FaTrash, FaSave, FaTag } from 'react-icons/fa';
import toast from 'react-hot-toast';
import './CategoriasGastoModal.css';
import {
  GET_CATEGORIAS_GASTO,
  CREAR_CATEGORIA_GASTO,
  ACTUALIZAR_CATEGORIA_GASTO,
  ELIMINAR_CATEGORIA_GASTO
} from '../../graphql/proveedores';

const COLORES_DISPONIBLES = [
  '#ef4444', '#f97316', '#f59e0b', '#eab308', '#84cc16',
  '#22c55e', '#10b981', '#14b8a6', '#06b6d4', '#3b82f6',
  '#1e40af', '#6366f1', '#8b5cf6', '#a855f7', '#ec4899',
  '#f43f5e', '#6b7280', '#374151'
];

const INITIAL_FORM = {
  nombre: '',
  descripcion: '',
  color: '#1e40af'
};

// Usage:
// <CategoriasGastoModal isOpen={open} onClose={() => setOpen(false)} />

const CategoriasGastoModal = ({ isOpen, onClose }) => {
  const [editingId, setEditingId] = useState(null);
  const [isCreating, setIsCreating] = useState(false);
  const [formData, setFormData] = useState(INITIAL_FORM);
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const { data, loading, refetch } = useQuery(GET_CATEGORIAS_GASTO, {
    fetchPolicy: 'network-only',
    skip: !isOpen
  });

  const [crearCategoria, { loading: creando }] = useMutation(CREAR_CATEGORIA_GASTO);
  const [actualizarCategoria, { loading: actualizando }] = useMutation(ACTUALIZAR_CATEGORIA_GASTO);
  const [eliminarCategoria, { loading: eliminando }] = useMutation(ELIMINAR_CATEGORIA_GASTO);

  const categorias = data?.categoriasGasto || [];

  useEffect(() => {
    if (!isOpen) {
      setEditingId(null);
      setIsCreating(false);
      setFormData(INITIAL_FORM);
    }
  }, [isOpen]);

  const handleEdit = (cat) => {
    setEditingId(cat.id);
    setIsCreating(false);
    setFormData({
      nombre: cat.nombre || '',
      descripcion: cat.descripcion || '',
      color: cat.color || '#1e40af'
    });
    setShowColorPicker(false);
  };

  const handleNuevaCategoria = () => {
    setIsCreating(true);
    setEditingId(null);
    setFormData(INITIAL_FORM);
    setShowColorPicker(false);
  };

  const handleCancel = () => {
    setIsCreating(false);
    setEditingId(null);
    setFormData(INITIAL_FORM);
    setShowColorPicker(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.nombre.trim()) {
      toast.error('El nombre de la categoria es obligatorio');
      return;
    }

    try {
      const input = {
        nombre: formData.nombre.trim(),
        descripcion: formData.descripcion.trim() || null,
        color: formData.color
      };

      if (editingId) {
        await actualizarCategoria({
          variables: { id: parseInt(editingId), input }
        });
        toast.success('Categoria actualizada');
      } else {
        await crearCategoria({ variables: { input } });
        toast.success('Categoria creada');
      }

      handleCancel();
      refetch();
    } catch (error) {
      console.error('Error al guardar categoria:', error);
      toast.error(error.message || 'Error al guardar la categoria');
    }
  };

  const handleDelete = async (cat) => {
    if (cat.cantidad_gastos > 0) {
      toast.error(
        `No se puede eliminar: la categoria tiene ${cat.cantidad_gastos} gasto(s) asociado(s)`
      );
      return;
    }

    setDeletingId(cat.id);
    try {
      await eliminarCategoria({ variables: { id: parseInt(cat.id) } });
      toast.success('Categoria eliminada');
      refetch();
    } catch (error) {
      console.error('Error al eliminar categoria:', error);
      toast.error(error.message || 'Error al eliminar la categoria');
    } finally {
      setDeletingId(null);
    }
  };

  if (!isOpen) return null;

  const savingForm = creando || actualizando;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="catgasto-modal" onClick={(e) => e.stopPropagation()}>
        <div className="catgasto-header">
          <h2>
            <FaTag />
            Categorias de Gasto
          </h2>
          <button className="close-button" onClick={onClose} type="button" aria-label="Cerrar">
            <FaTimes />
          </button>
        </div>

        <div className="catgasto-body">
          {/* Action bar */}
          <div className="catgasto-toolbar">
            {!isCreating && !editingId ? (
              <button className="btn-nueva-cat" onClick={handleNuevaCategoria} type="button">
                <FaPlus /> Nueva Categoria
              </button>
            ) : (
              <span className="catgasto-form-title">
                {editingId ? 'Editando categoria' : 'Nueva categoria'}
              </span>
            )}
          </div>

          {/* Form */}
          {(isCreating || editingId) && (
            <form className="catgasto-form" onSubmit={handleSubmit}>
              <div className="catgasto-form-row">
                {/* Color picker */}
                <div className="form-group">
                  <label>Color</label>
                  <div className="color-picker-wrapper">
                    <button
                      type="button"
                      className="color-preview-btn"
                      style={{ backgroundColor: formData.color }}
                      onClick={() => setShowColorPicker((v) => !v)}
                      title="Cambiar color"
                    />
                    {showColorPicker && (
                      <div className="color-palette">
                        {COLORES_DISPONIBLES.map((c) => (
                          <button
                            key={c}
                            type="button"
                            className={`color-swatch ${formData.color === c ? 'color-swatch--active' : ''}`}
                            style={{ backgroundColor: c }}
                            onClick={() => {
                              setFormData((p) => ({ ...p, color: c }));
                              setShowColorPicker(false);
                            }}
                            title={c}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                <div className="form-group form-group--flex">
                  <label htmlFor="cg-nombre">Nombre *</label>
                  <input
                    id="cg-nombre"
                    type="text"
                    value={formData.nombre}
                    onChange={(e) => setFormData((p) => ({ ...p, nombre: e.target.value }))}
                    placeholder="Ej: Servicios Publicos"
                    required
                  />
                </div>

                <div className="form-group form-group--flex">
                  <label htmlFor="cg-descripcion">Descripcion</label>
                  <input
                    id="cg-descripcion"
                    type="text"
                    value={formData.descripcion}
                    onChange={(e) => setFormData((p) => ({ ...p, descripcion: e.target.value }))}
                    placeholder="Descripcion breve (opcional)"
                  />
                </div>
              </div>

              <div className="catgasto-form-actions">
                <button type="submit" className="btn-guardar-cat" disabled={savingForm}>
                  <FaSave />
                  {savingForm ? 'Guardando...' : editingId ? 'Actualizar' : 'Crear'}
                </button>
                <button type="button" className="btn-cancelar-cat" onClick={handleCancel}>
                  Cancelar
                </button>
              </div>
            </form>
          )}

          {/* Categories list */}
          <div className="catgasto-lista">
            <h3>Categorias Existentes ({categorias.length})</h3>

            {loading ? (
              <div className="catgasto-loading">Cargando categorias...</div>
            ) : categorias.length === 0 ? (
              <div className="catgasto-empty">
                <FaTag />
                <p>No hay categorias creadas. Crea la primera para organizar tus gastos.</p>
              </div>
            ) : (
              <div className="catgasto-items">
                {categorias.map((cat) => (
                  <div
                    key={cat.id}
                    className={`catgasto-item ${!cat.activa ? 'catgasto-item--inactiva' : ''} ${editingId === cat.id ? 'catgasto-item--editing' : ''}`}
                  >
                    <div
                      className="catgasto-color-swatch"
                      style={{ backgroundColor: cat.color || '#6b7280' }}
                    />

                    <div className="catgasto-item-info">
                      <div className="catgasto-item-nombre">{cat.nombre}</div>
                      {cat.descripcion && (
                        <div className="catgasto-item-descripcion">{cat.descripcion}</div>
                      )}
                    </div>

                    <div className="catgasto-item-stats">
                      {cat.cantidad_gastos > 0 && (
                        <span className="stat-badge">
                          {cat.cantidad_gastos} gasto{cat.cantidad_gastos !== 1 ? 's' : ''}
                        </span>
                      )}
                      {!cat.activa && <span className="inactiva-badge">Inactiva</span>}
                    </div>

                    <div className="catgasto-item-actions">
                      <button
                        type="button"
                        className="btn-icon btn-icon--edit"
                        onClick={() => handleEdit(cat)}
                        title="Editar"
                        disabled={!!editingId && editingId !== cat.id}
                      >
                        <FaEdit />
                      </button>
                      <button
                        type="button"
                        className="btn-icon btn-icon--delete"
                        onClick={() => handleDelete(cat)}
                        title={cat.cantidad_gastos > 0 ? 'Tiene gastos asociados' : 'Eliminar'}
                        disabled={eliminando && deletingId === cat.id}
                      >
                        <FaTrash />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CategoriasGastoModal;
